<template>
  <loading-page
    v-if="initialLoading"
    full
  />
  <div
    v-else
    class="table-wrapper alerts"
  >
    <div class="d-block d-sm-flex alerts-header">
      <div>
        <b-button
          v-if="$can('create', 'alerts', 'snitch')"
          variant="bg-primary"
          @click="onCreateAlert()"
        >
          <GjIcon
            name="Plus"
            class="icon-left"
          />
          Create Alert
        </b-button>
      </div>
      <div>
        <b-button
          v-if="$can('update', 'alerts', 'snitch')"
          variant="bg-white"
          :disabled="!isSelected || removingAlert"
          class="d-none d-lg-inline action-alert-button"
          @click="onEditClick(selected[0].id)"
        >
          Edit
        </b-button>
        <button-loading
          v-if="$can('delete', 'alerts', 'snitch')"
          variant="bg-white"
          :disabled="!isSelected || removingAlert"
          :spinning="removingAlert"
          class="d-none d-lg-inline action-alert-button"
          @click="onDeleteClick(selected[0].id)"
        >
          Delete
        </button-loading>
      </div>
      <button-loading
        variant="bg-white"
        class="mt-1 mt-sm-0 ml-auto mr-0"
        :spinning="loading"
        :disabled="loading || removingAlert"
        @click="getAlerts()"
      >
        <GjIcon
          class="icon-left"
          name="Refresh"
        />
        Refresh
      </button-loading>
    </div>
    <!-- DESKTOP -->
    <div class="default-table position-relative d-none d-lg-block">
      <b-table
        ref="table"
        :items="alerts"
        :fields="fields"
        select-mode="single"
        show-empty
        selectable
        responsive
        :class="{ loaded: !loading }"
        :busy="loading"
        @row-selected="onRowSelected"
      >
        <template #cell(selected)="{ rowSelected }">
          <select-button
            :selected="rowSelected"
            class="d-flex justify-content-center"
          />
        </template>

        <template #cell(name)="data">
          <a
            v-if="$can('get', 'alerts', 'snitch')"
            class="link"
            @click="onDetailsClick(data.item.id)"
          >
            {{ data.item.name }}
          </a>
          <span v-else>
            {{ data.item.name }}
          </span>
        </template>

        <template #cell(providers)="data">
          <b-avatar-group
            v-if="data.item.providers.length > 0"
            size="42px"
            overlap="0"
          >
            <div
              v-for="(provider, index) in groupAlertProviders(
                data.item.providers,
              )"
              :key="index"
            >
              <b-avatar
                v-b-tooltip.hover
                class="ml-1 provider-type-badge pull-up"
                :badge="provider.childs.length.toString()"
                :title="returnProviderTitles(provider)"
                :src="changeTypeSrc(provider.type)"
              />
            </div>
          </b-avatar-group>
          <div v-else>
            --
          </div>
        </template>

        <template #cell(healthcheck)="data">
          <span
            v-if="!data.item.healthcheck"
            class="text-danger"
          >
            Healthcheck not found!
          </span>
          <a
            v-else
            class="link"
            @click="onHealthcheckClick(data.item.healthcheck.id)"
          >
            {{ data.item.healthcheck.name }}
          </a>
        </template>

        <template #cell(updateDate)="data">
          {{ formatOffsetDateTime(data.value) }}
        </template>

        <template #empty>
          <div
            v-if="loading || removingAlert"
            class="loading-table text-center py-1"
          >
            <b-spinner variant="primary" />
          </div>
          <template v-else>
            <no-data
              no-data-text="No Alerts have been added yet..."
              create-text="+ Create Alert"
              access-page="Alerts"
              icon="Notifications"
              :has-permissions="hasAlertPermissions"
              @action="onCreateAlert()"
            />
          </template>
        </template>
      </b-table>
      <table-busy v-if="loading && alerts.length > 0" />
      <div
        v-if="alerts.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
    <!-- MOBILE -->
    <div class="position-relative d-lg-none mt-2">
      <div :class="{ 'opacity-7': loading || removingAlert }">
        <template v-for="alert in alerts">
          <card
            :key="alert.id"
            :fields="fields"
            :item="alert"
            class="top-fade-in"
          >
            <template #options>
              <b-dropdown
                id="dropdown-1"
                right
                variant="white"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'alerts', 'snitch')"
                  @click="onEditClick(alert.id)"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'alerts', 'snitch')"
                  @click="onDeleteClick(alert.id)"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>

            <template #name>
              <a
                v-if="$can('get', 'alerts', 'snitch')"
                class="link"
                @click="onDetailsClick(alert.id)"
              >
                {{ alert.name }}
              </a>
              <span v-else>
                {{ alert.name }}
              </span>
            </template>

            <template #providers>
              <b-avatar-group
                v-if="alert.providers.length > 0"
                size="42px"
                overlap="0"
              >
                <div
                  v-for="(provider, index) in groupAlertProviders(
                    alert.providers,
                  )"
                  :key="index"
                >
                  <b-avatar
                    v-b-tooltip.hover
                    class="ml-1 provider-type-badge pull-up"
                    :badge="provider.childs.length.toString()"
                    :title="returnProviderTitles(provider)"
                    :src="changeTypeSrc(provider.type)"
                  />
                </div>
              </b-avatar-group>
              <div v-else>
                --
              </div>
            </template>

            <template #healthcheck>
              <span
                v-if="!alert.healthcheck"
                class="text-danger"
              >
                Healthcheck not found!
              </span>
              <a
                v-else
                class="link"
                @click="onHealthcheckClick(alert.healthcheck.id)"
              >
                {{ alert.healthcheck.name }}
              </a>
            </template>

            <template #updateDate>
              {{ formatOffsetDateTime(alert.updateDate) }}
            </template>
          </card>
        </template>
      </div>
      <div
        v-if="loading || removingAlert"
        class="mobile-loading"
      >
        <table-busy />
      </div>
      <template v-if="!loading && alerts.length < 1">
        <paper-simple class="top-fade-in">
          <no-data
            class="text-center"
            no-data-text="No alerts have been added yet..."
            create-text="+ Create Alert"
            access-page="Alerts"
            icon="Notifications"
            :has-permissions="hasAlertPermissions"
            @action="onCreateAlert()"
          />
        </paper-simple>
      </template>
      <div
        v-if="alerts.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
    <create-form
      v-if="showCreateForm"
      @close="showCreateForm = false"
      @onRefresh="addAlert"
    />
    <alert-details
      v-if="showAlertDetails"
      :alert-id="alertId"
      @close="showAlertDetails = false"
      @editAlert="editAlert"
    />
    <edit-form
      v-if="showEditForm"
      :alert-id="alertId"
      details
      @close="showEditForm = false"
      @onRefresh="updateAlert"
    />
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import CreateForm from './create/Create.vue'
import EditForm from './edit/Edit.vue'
import AlertDetails from './details/AlertDetails.vue'

export default {
  name: 'Alerts',
  components: {
    Card,
    CreateForm,
    EditForm,
    AlertDetails,
  },
  data() {
    return {
      initialLoading: true,
      alertId: 0,
      showEditForm: false,
      showAlertDetails: false,
      showCreateForm: false,
      selected: [],
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'name', isPrimary: true },
        { key: 'providers', isPrimary: true },
        { key: 'healthcheck', label: 'Healthcheck' },
        { key: 'updateDate', label: 'Last Modified' },
      ],
      hasAlertPermissions: true,
      /* eslint-disable global-require */
      googleChat: require('@/assets/svg/icons/google-chat.svg'),
      slack: require('@/assets/svg/icons/slack.svg'),
      discord: require('@/assets/svg/icons/discord.svg'),
      /* eslint-enable global-require */
    }
  },
  computed: {
    loading() {
      return this.$store.state.alerts.fetchingAlerts
    },
    alerts() {
      return this.$store.state.alerts.alerts
    },
    removingAlert() {
      return this.$store.state.alerts.removingAlert
    },
    isSelected() {
      return this.selected[0]
    },
    totalCount() {
      return this.resultInfo?.totalCount
    },
    resultInfo() {
      return this.$store.state.alerts.alertsResultInfo
    },
    pageSize() {
      return this.resultInfo?.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentPage: {
      get() {
        return this.$store.getters['alerts/currentPage']
      },
      set(value) {
        this.$store.commit('alerts/SET_CURRENT_PAGE', value)
      },
    },
  },
  watch: {
    currentPage() {
      this.getAlerts()
    },
  },
  created() {
    this.getAlerts()
  },
  methods: {
    onRowSelected(item) {
      this.selected = item
    },
    onHealthcheckClick(healthcheckId) {
      this.$router.push({
        name: 'healthchecks-details',
        params: { healthcheckId },
      })
    },
    onCreateAlert() {
      this.showCreateForm = true
    },
    onDetailsClick(alertId) {
      this.alertId = alertId
      this.showAlertDetails = true
    },
    onEditClick(alertId) {
      this.alertId = alertId
      this.showEditForm = true
    },
    editAlert(alertId) {
      this.alertId = alertId
      this.showAlertDetails = false
      this.showEditForm = true
    },
    addAlert() {
      this.getAlerts()
      this.showCreateForm = false
    },
    updateAlert() {
      this.getAlerts()
      this.showEditForm = false
    },
    groupAlertProviders(providers) {
      const types = [...new Set(providers.map(el => el.type))]
      const arr = []
      types.forEach(type => {
        const childs = providers.filter(el => el.type === type)
        arr.push({ type, childs })
      })
      return arr
    },
    changeTypeSrc(type) {
      if (type === 0) {
        return this.googleChat
      }
      if (type === 1) {
        return this.slack
      }
      if (type === 2) {
        return this.discord
      }
      return null
    },
    returnProviderTitles(provider) {
      let title = ''
      provider.childs.forEach((e, index) => {
        if (index !== 0) {
          title += `, ${e.name}`
        } else {
          title += e.name
        }
      })
      return title
    },
    onDeleteClick(alertId) {
      this.confirmDelete(
        'Delete Alert',
        'Are you sure you want to delete this alert?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('alerts/removeAlert', alertId)
            .then(() => {
              this.successToast('Alert was removed!')
              this.selected = []
              if (this.alerts.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.getAlerts()
              }
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting alert.')
            })
        }
      })
    },
    getAlerts() {
      this.$store
        .dispatch('alerts/fetchAlerts', {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 426 || status === 428) {
            this.$store.commit('observability/SET_SERVICE_UNAVAILABLE', { serviceUnavailable: true, serviceRequested: status === 428 })
            return
          }
          if (status === 403) {
            this.hasAlertPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching alerts!',
          )
        })
        .finally(() => { this.initialLoading = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.alerts {
  .provider-type-badge {
    background-color: #fff !important;
    border: 1px solid #f6f7f9;
  }
  ::v-deep {
    td {
      padding: 7px !important;
    }
  }
}
</style>
